import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles, Group, Stack, Text} from '@mantine/core';
import React from 'react';

import {getChatWorkflowCategories, type ChatUseCase} from 'llm/chat-library/use-cases/chatUseCaseCatalog';
import type {FoundryUseCaseRunSource} from 'llm/chat-library/use-cases/recordChatUseCaseRun';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import MantineDropdownMenu, {
  DropdownMenuWidth,
  type MantineDropdownMenuOption,
} from 'ui-library/menus/MantineDropdownMenu';

interface HomepageEntryPointProps {
  onStartFoundryChat: (chatUseCase: ChatUseCase, source: FoundryUseCaseRunSource) => void;
  variant: 'homepage';
}

type CreateAgentFoundryEntryPointProps = HomepageEntryPointProps;

const useHomepageStyles = createStyles((theme) => ({
  dropdown: {
    maxWidth: `calc(100vw - ${theme.spacing.xl} - ${theme.spacing.xl})`,
  },
  menuItem: {
    whiteSpace: 'normal',
  },
  menuItemIcon: {
    flexShrink: 0,
    fontSize: theme.fontSizes.md,
    height: theme.fontSizes.md,
    width: theme.fontSizes.md,
  },
  menuItemDescription: {
    color: theme.colors.gray[6],
    lineHeight: theme.lineHeight,
    marginLeft: `calc(${theme.fontSizes.md} + ${theme.spacing.sm})`,
  },
}));

export default function CreateAgentFoundryEntryPoint(props: CreateAgentFoundryEntryPointProps): JSX.Element {
  return <HomepageEntryPoint onStartFoundryChat={props.onStartFoundryChat} />;
}

function HomepageEntryPoint({onStartFoundryChat}: Omit<HomepageEntryPointProps, 'variant'>): JSX.Element {
  const {classes} = useHomepageStyles();
  const foundryCategory = getChatWorkflowCategories().find(({id}) => id === 'create-foundry');
  if (foundryCategory == null) {
    return <></>;
  }

  const foundryMenuOptions: Array<MantineDropdownMenuOption> = foundryCategory.chatUseCases.map(
    (chatUseCase, index) => ({
      name: '',
      dataTestAttrs: {
        component: 'CreateAgentFoundryEntryPoint',
        element: 'useCaseOption',
        key: chatUseCase.id,
        index: String(index),
      },
      onSelect: () => {
        onStartFoundryChat(chatUseCase, 'foundry_page_button');
      },
      children: (
        <Stack spacing={2}>
          <Group align="center" noWrap={true} spacing="sm">
            <BenchlingIcon aria-hidden={true} className={classes.menuItemIcon} icon={chatUseCase.icon} />
            <Text>{chatUseCase.label}</Text>
          </Group>
          <Text className={classes.menuItemDescription} size="sm">
            {chatUseCase.description}
          </Text>
        </Stack>
      ),
    })
  );

  return (
    <MantineDropdownMenu
      itemClassName={classes.menuItem}
      position="bottom-end"
      rootClassName={classes.dropdown}
      sections={[
        {
          items: foundryMenuOptions,
        },
      ]}
      trigger={
        <Button
          rightIcon={<BenchlingIcon icon="chevron-down" />}
          size="xs"
          {...getTestProps({component: 'CreateAgentFoundryEntryPoint', element: 'homepageButton'})}
        >
          New foundry
        </Button>
      }
      width={DropdownMenuWidth.XXLARGE}
    />
  );
}
